// GalleryCardRenderer.jsx

import React from 'react';

// Components
import GalleryViewNodeCard from './GalleryViewNodeCard';
import { isSketchType } from 'utilities/sketchType';

import PhotoCard from './PhotoCard';
import VideoCard from './VideoCard';

/**
 * GalleryCardRenderer
 *
 * Renders the appropriate gallery card for a single node.
 *
 * @param {object} node - Node row to render.
 * @param {object|null} typeDef - Active type definition fallback.
 * @param {array} cardFieldKeys - Field keys to show on data cards.
 * @param {Map} schemaFieldMap - Schema field lookup for data cards.
 * @param {function} resolveFieldValue - Resolves field values for data cards.
 * @param {function} formatFieldValue - Formats field values for data cards.
 * @param {boolean} [showCover=true] - Whether to render the cover surface for data cards.
 * @param {boolean} [showSubtitle=true] - Whether to render the subtitle surface for data cards.
 * @param {boolean} [showNoteContents=true] - Whether to render note contents on data cards.
 * @param {object} theme - Theme object for GalleryViewNodeCard.
 * @param {function} [onAspectRatio] - Optional media aspect ratio callback.
 *
 * @returns {ReactNode} The rendered gallery card.
 */
const GalleryCardRenderer = React.memo(({
    cardFieldKeys,
    formatFieldValue,
    node,
    onAspectRatio,
    resolveFieldValue,
    schemaFieldMap,
    showCover = true,
    showSubtitle = true,
    showNoteContents = true,
    theme,
    typeDef,
}) => {
    const typeKey = String(node?.type || typeDef?.key || '').toUpperCase();

    if (typeKey === 'PHOTO' || isSketchType(typeKey)) {
        return <PhotoCard node={node} onAspectRatio={onAspectRatio} />;
    }

    if (typeKey === 'VIDEO') {

        return <VideoCard node={node} onAspectRatio={onAspectRatio} />;
    }
    // ----- RENDER ----- //

    return (
        <GalleryViewNodeCard
          cardFieldKeys={cardFieldKeys}
          formatFieldValue={formatFieldValue}
          node={node}
          resolveFieldValue={resolveFieldValue}
          schemaFieldMap={schemaFieldMap}
          showCover={showCover}
          showSubtitle={showSubtitle}
          showNoteContents={showNoteContents}
          theme={theme}
        />
    );
});

// Dev Tools
GalleryCardRenderer.displayName = 'GalleryCardRenderer';

export default GalleryCardRenderer;
